/* Data for scenario SC8CP phase IE 
This data structure holds all the information to create the slides to present during the IE phase.
It mirrors the tree structure that is presented in the Excel file. All nodes in the tree are called actions whether 
they are assessments or interventions. Subactions are the children of the actions in the tree.
Fields:
id: identifies the action-node in the tree. It is a string since it is unique.
label: text for the action as it will appear on the slide
type: indicates whether the action is Required, Not indicated, Optional, Alternative.
    Required: students are asked if they performed it.
    Not indicated: it will be listed on a list of not indicated actions that the student performed accompanied by 
    the feedback on the feedbackErrors field.
    Optional: it will be accepted as correct if performed.
    Alternative: will be used for those interventions that can be performed as alternative to required ones.
    [
    Not used for the pilot study but was used for trial 1:
    Contraindicated: it will be listed under a list of contraindicated actions that the student performed
    accompanied by the feedback on the feedbackErrors field.
    Unnecessary: it will be listed under a list of unnecessary actions that the student performed
    accompanied by the feedback on the feedbackErrors field.
    Irrelevent: it will be listed under a list of irrelevant actions that the student performed
    accompanied by the feedback on the feedbackErrors field.
    ]
alternativeToIntv: indicates the intervention for which the current one is an alternative. It will only have a value if
the type is Alternative.
feedbackAbsent: feedback given if the action was no performed. Each element in the array corresponds to a paragraph.
feedbackOutOfOrder: feedback given if the action was done late (e.g., if the action is Scene size-up and it was done 
    after Primary Survey). Each element in the array corresponds to a paragraph.
feedbackErrors: feedback if the action was performed incorrectly (e.g., wrong ventilation rate). Each element in the array
 corresponds to a paragraph.
examineIfAbsent: boolean to indicate if this Action needs to be examined when it is Absent (phase E)
examineIfErrors: boolean to indicate if this Action needs to be examined when there are performance errors (phase E)
promptsIfAbsent: prompts to be included in question headers in phase E, to hint on how to reflect when Action was Absent
promptsIfErrors: prompts to be included in question headers in phase E, to hint on how to reflect when Action was performed incorrectly
subActionsList: boolean that indicates whether the subActions need to be listed as bullet points on the slide. If they
    don't need to be listed it is usually because they have been grouped however, the subActions still need to be 
    included in the subActions field because they are used by the analysis to give a summary of their status.
subActions: array with the corresponding subactions (to form the tree)
*/

import { ACTION_TYPES, WAIT_TIFF, WAIT_SANDY, SUM_NODE_NO_FEEDBACK, NO_FEEDBACK, SUM_NODE_FBCK_CHILDREN } from "./constants";

export const SC8CP_PhaseIE = {
    Action: {
      id: "top",
      label: "fully assess and manage the patient in this scenario", /* the way it will appear on the slide */
      type: ACTION_TYPES.REQ, /*indicates whether the action was Required, Contraindicated, Unnecessary, or Irrelevant" */
      feedbackAbsent: ["Your log indicates you didn't complete any of these assessment or managment goal. Please restart the scenario and try again"], /*feedback if the action was not performed*/
      feedbackOutOfOrder: [""], /*feedback if the action was done late (e.g., if the action is Scene Size-up 
        and it was done after Primary Survey)*/
      feedbackErrors: [""], /*feedback if the action was performed incorrectly (e.g. wrong Ventilation rate)*/
      examineIfAbsent: false, /* boolean to indicate if this Action needs to be examined when it is Absent (phase E) */
      examineIfErrors: false, /* boolean to indicate if this Action needs to be examined when there are performance errors */
      promptsIfAbsent: "", /* prompts to the included in question in phase E to hint on how to reflect */
      promptsIfErrors: "",
      subActionsList: true, /* boolean that indicates if subActions need to be listed as bullet points on slide */
      subActions: [ /*array with the corresponding subactions (to form the tree)*/
        {
          id: "scene-size-up",
          label: "Put on BSI and do a complete Scene Size-up before entering the scene",
          type: ACTION_TYPES.REQ,
          feedbackAbsent: ["Your first steps should have been to don your protective wear, including eye protection, and to ensure the scene is safe. You cannot assist a patient if you are not safe yourself.", "Your scene size up gives you a big-picture view of what is going on before you even begin examining the patient and includes determining the mechanism of injury and number of patients, requesting additional help if necessary, and considering stabilization of the spine.", "In this case, you would have learned that this is a near drowning incident, which should have prompted you to think about potential head or spinal injury, and the need for rescue breaths and/or CPR."],
          feedbackOutOfOrder: ["Your first steps should have been to don your protective wear, including eye protection, and to ensure the scene is safe. You cannot assist a patient if you are not safe yourself.", "Your scene size up also gives you a big-picture view of what is going on before you even begin examining the patient. This includes determining the mechanism of injury and number of patients, requesting additional help if necessary, and considering stabilization of spine."],
          feedbackErrors: [""],
          examineIfAbsent: false,
          examineIfErrors: false,
          promptsIfAbsent: "",
          promptsIfErrors: "",
          subActionsList: true,
          subActions: [
            {
                id: "required-action-BSI",
                label: "Apply BSI",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["Your first steps should have been to don your protective wear, including eye protection, and to ensure the scene is safe. You cannot assist a patient if you are not safe yourself."],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: [],
              },
              {
                id: "assess-scene-safety",
                label: "Determine the scene/situation is safe",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["Your first steps should have been to don your protective wear, including eye protection, and to ensure the scene is safe. You cannot assist a patient if you are not safe yourself."],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: [],
              },
              {
                id: "assess-injury-mechanism",
                label: "Determine the mechanism of injury (MOI) or nature of illness (NOI)",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["Knowing what caused the patient's injury or illness can allow you to start developing a plan of action before you even assess your patient.", "By determining the mechanism of injury, you would have learned that this is a near drowning incident, which should have prompted you to think about potential head or spinal injury, and the need for rescue breaths and/or CPR."],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: [],
              },
              {
                id: "assess-num-patients",
                label: "Determine the number of patients",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["Usually you will have one patient, but if there is more than one you will need to plan accordingly."],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: [],
              },
              {
                id: "required-action-requests-additional-help",
                label: "Request additional help if necessary",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["Consider the need for extra help based on number of patients, mechanism of injury, and your patient's status.", "Although police and fire personnel were already on scene, you should have considered the need for additional ALS support."],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: [],
              },
              {
                id: "required-action-considers-stabilize-spine",
                label: "Consider stabilization of spine",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["All trauma patients are at risk for spinal injury, including drowning or near drowning patients, which could be caused by a diving accident. Take this into consideration before initial contact with the patient to prevent unintentional injury."],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: [],
              },
          ],
        },
        {
          id: "primary-survey",
          label: "Perform a Primary Survey and manage all identified life threats",
          type: ACTION_TYPES.REQ,
          feedbackAbsent: ["The primary survey is an assessment of mental status, life-threats, and the patient's ABCs. It is where you will identify and address the patient's most serious injuries.", "In this case, the Primary Survey would have revealed an unconscious patient with respiratory failure and a weak and slow pulse."],
          feedbackOutOfOrder: ["You should have started your Primary Survey after the Scene Size-up and completed it before starting the Secondary Survey. Only interrupt your assessment to control life-threats: (1) Conditions that compromise a patent airway (2) Conditions that compromise breathing or respirations, such as a tension pneumothorax, (3) Conditions that compromise circulation, such as severe bleeding, (4) Cardiac arrest, and any  other potentially life threatening injuries or conditions.  Your patient's condition will deteriorate if these conditions are not addressed before continuing on with your assessment and history taking."],
          feedbackErrors: [""],
          examineIfAbsent: false,
          examineIfErrors: false,
          promptsIfAbsent: "",
          promptsIfErrors: "",
          subActionsList: true,
          subActions: [
            {
                id: "general-impression",
                label: "Conduct an Initial Assessment",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["You should have performed an Initial Assessment: gathered a general impression, identified any apparent life threats, and determined your patient's level of consciousness.  Your Initial Assessment  would have revealed a patient who is unconscious."],
                feedbackOutOfOrder: ["While you may be tempted to jump straight into your ABCs, apparent life threats (i.e., uncontrolled bleeding) should be addressed first, as they can cause a patient to deteriorate quickly. Also, be sure to assess the patient's level of consciousness as an unconscious patient will need a pulse check first to ensure he or she does not require CPR."],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: true,
                subActions: [
                    {
                        id: "assess-patient-condition",
                        label: "State or request a general impression",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["Your general impression allows you to start developing a plan of action before you even assess your patient.", "In this case, your general impression would have been of a child who nearly drowned."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "assess-loc",
                        label: "Assess the patient's level of consciousness",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["The patient's level of consciousness alters how you progress through your assessment.", "This patient was unresponsive with sluggish pupils.  This should have prompted you to consider him a critical patient, check his pulse prior to airway and breathing, and plan for urgent transfer to a nearby trauma center."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "assess-life-threats",
                        label: "Determine the chief complaint/apparent life-threats",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["If you had determined apparent life threats, you would have noticed that the patient was unresponsive, indicating a critical patient."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                ],
              },
              {
                id: "circulation",
                label: "follow a 'CAB' protocol, given that this patient is unconscious, and first assess the patient's pulse, followed by other aspects of circulation",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["Since this patient is unconscious, be sure to check the carotid pulse first, or simultaneously with the radial pulse, to determine if CPR is needed.  Also assess the skin to check for shock and be sure to perform a gross blood sweep.", "You would have noticed that your patient had weak slow pulses and dry cyanotic skin, indicating impending respiratory failure."],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: true,
                subActions: [
                  {
                    id: "pulse-checks",
                    label: "check pulse",
                    type: ACTION_TYPES.REQ,
                    feedbackAbsent: ["First check for a radial pulse. However, if you don't feel one, you want to check a carotid pulse.", "This patient's radial pulses were absent, but he did have a weak carotid pulse, which would prompt you to worry about shock.", "Checking this patient's pulse rate would have revealed that he was tachycardic. A significantly elevated or depressed pulse rate can suggest shock or respiratory failure.", "While this patient's heart had a regular rhythm, remember that an irregular rhythm can suggest impending cardiac arrest or arrhythmia.", "A patient in shock may have weak or absent pulses distally, such as this patient, suggestive of shock."],
                    feedbackOutOfOrder: ["All unconscious patients should have a pulse check before starting the ABCs. However, paramedics often check breathing while doing a pulse check.  If the patient is pulseless, you will start high-quality CPR immediately.", "An acronym used by some providers for unconscious patients is CABC, which refers to a pulse check (while simultaneously feeling for breathing) before the assessment of the rest of the ABCs."],
                    feedbackErrors: [""],
                    examineIfAbsent: false,
                    examineIfErrors: false,
                    promptsIfAbsent: "",
                    promptsIfErrors: "",
                    subActionsList: true,
                    subActions: [
                      {
                        id: "prompt-to-specify-pulse",
                        label: "check the appropriate pulse(s)",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["First check for a radial pulse. However, if you don't feel one, you want to check a carotid pulse.", "This patient's radial pulses were absent, but he did have a weak carotid pulse, which would prompt you to worry about shock."],
                        feedbackOutOfOrder: ["All unconscious patients should have a pulse check before starting the ABCs. However, paramedics often check breathing while doing a pulse check.  If the patient is pulseless, you will start high-quality CPR immediately.", "An acronym used by some providers for unconscious patients is CABC, which refers to a pulse check (while simultaneously feeling for breathing) before the assessment of the rest of the ABCs."],                        
                        feedbackErrors: ["When you have an unconscious patient, the first thing you will do (before checking the airway) is to check a carotid pulse. Checking for a radial pulse is not the best use of time because a patient who is hypo-perfused may have absent distal pulses but intact central pulses; this patient would not require CPR."],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "checks-pulse-rate-rhythm-quality",
                        label: "check pulse rate, rhythm, and quality",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["Checking this patient's pulse rate would have revealed that he was tachycardic. A significantly elevated or depressed pulse rate can suggest shock or respiratory failure.", "While this patient's heart had a regular rhythm, remember that an irregular rhythm can suggest impending cardiac arrest or arrhythmia.", "A patient in shock may have weak or absent pulses distally, such as this patient, suggestive of shock."],
                        feedbackOutOfOrder: ["All unconscious patients should have a pulse check before starting the ABCs. However, paramedics often check breathing while doing a pulse check.  If the patient is pulseless, you will start high-quality CPR immediately.", "An acronym used by some providers for unconscious patients is CABC, which refers to a pulse check (while simultaneously feeling for breathing) before the assessment of the rest of the ABCs."],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [
                          {
                            id: "checks-pulse-rate",
                            label: "rate",
                            type: ACTION_TYPES.REQ,
                            feedbackAbsent: ["Checking this patient's pulse rate would have revealed that he was tachycardic. A significantly elevated or depressed pulse rate can suggest shock or respiratory failure."],
                            feedbackOutOfOrder: ["All unconscious patients should have a pulse check before starting the ABCs. However, paramedics often check breathing while doing a pulse check.  If the patient is pulseless, you will start high-quality CPR immediately.", "An acronym used by some providers for unconscious patients is CABC, which refers to a pulse check (while simultaneously feeling for breathing) before the assessment of the rest of the ABCs."],
                            feedbackErrors: [""],
                            examineIfAbsent: false,
                            examineIfErrors: false,
                            promptsIfAbsent: "",
                            promptsIfErrors: "",
                            subActionsList: false,
                            subActions: [],
                          },
                          {
                            id: "checks-pulse-rhythm",
                            label: "rhythm",
                            type: ACTION_TYPES.REQ,
                            feedbackAbsent: ["While this patient's heart had a regular rhythm, remember that an irregular rhythm can suggest impending cardiac arrest or arrhythmia."],
                            feedbackOutOfOrder: [""],
                            feedbackErrors: [""],
                            examineIfAbsent: false,
                            examineIfErrors: false,
                            promptsIfAbsent: "",
                            promptsIfErrors: "",
                            subActionsList: false,
                            subActions: [],
                          },
                          {
                            id: "checks-pulse-quality",
                            label: "quality",
                            type: ACTION_TYPES.REQ,
                            feedbackAbsent: ["A patient in shock may have weak or absent pulses distally, such as this patient, suggestive of shock."],
                            feedbackOutOfOrder: [""],
                            feedbackErrors: [""],
                            examineIfAbsent: false,
                            examineIfErrors: false,
                            promptsIfAbsent: "",
                            promptsIfErrors: "",
                            subActionsList: false,
                            subActions: [],
                          },
                        ],
                      },
                    ],
                  },
                      {
                        id: "checks-skin",
                        label: "check skin",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["In this case, the patient had been pulled from a pool, making a skin exam challenging, but remember that clammy, diaphoretic skin suggests shock. Also, by checking this patient's skin color, you would have noticed he had dry, cyanotic skin. Pallor and cyanosis are signs of inadequate oxygenation and may be caused by respiratory failure or shock.", "Finally, by checking skin temperature, you would have noticed cold skin, which could be because of inadequate perfusion (decompensated shock) or environmental factors."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: true,
                        subActions: [
                          {
                            id: "checks-skin-color",
                            label: "color",
                            type: ACTION_TYPES.REQ,
                            feedbackAbsent: ["By checking this patient's skin color, you would have noticed he had dry, cyanotic skin. Pallor and cyanosis are signs of inadequate oxygenation and may be caused by respiratory failure or shock."],
                            feedbackOutOfOrder: [""],
                            feedbackErrors: [""],
                            examineIfAbsent: false,
                            examineIfErrors: false,
                            promptsIfAbsent: "",
                            promptsIfErrors: "",
                            subActionsList: false,
                            subActions: [],
                          },
                          {
                            id: "checks-skin-temperature",
                            label: "temperature",
                            type: ACTION_TYPES.REQ,
                            feedbackAbsent: ["By checking skin temperature, you would have noticed cold skin, which could be because of inadequate perfusion (decompensated shock) or environmental factors."],
                            feedbackOutOfOrder: [""],
                            feedbackErrors: [""],
                            examineIfAbsent: false,
                            examineIfErrors: false,
                            promptsIfAbsent: "",
                            promptsIfErrors: "",
                            subActionsList: false,
                            subActions: [],
                          },
                          {
                            id: "checks-skin-condition",
                            label: "condition",
                            type: ACTION_TYPES.REQ,
                            feedbackAbsent: ["In this case, the patient had been pulled from a pool, making a skin exam challenging, but remember that clammy, diaphoretic skin suggests shock."],
                            feedbackOutOfOrder: [""],
                            feedbackErrors: [""],
                            examineIfAbsent: false,
                            examineIfErrors: false,
                            promptsIfAbsent: "",
                            promptsIfErrors: "",
                            subActionsList: false,
                            subActions: [],
                          },
                        ],
                      },
                      {
                        id: "assess-major-bleeding",
                        label: "Perform a gross blood sweep",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["A gross blood sweep, which was negative in this patient, will help you identify any life-threatening bleeding you may have missed earlier when forming your general impression."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                ],
              },
              {
                id: "Manage a potential head or spinal injury",
                label: "Manage a potential head or spinal injury",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["Although the exact MOI is unknown, you should suspect possible head and/or spinal injury with any near drowning incident as diving may have been involved. You should have taken manual c-spine stabilization before performing your ABC assessment and maintained it throughout and applied an appropriately sized cervical collar."],
                feedbackOutOfOrder: ["Managing the cervical spine is one of the first things you should do for any trauma patient, as you must ensure you do not cause any further injury during the remainder of your assessment and management."],
                feedbackErrors: [""],
                examineIfAbsent: true,
                examineIfErrors: false,
                promptsIfAbsent: "didn't recall that spinal injury should be considered in every near drowning incident; did not consider risk for cervical spine injury based on provided information",
                promptsIfErrors: "",
                subActionsList: true,
                subActions: [
                    {
                        id: "intv-spinal-immobilization-technique-manual-c-spine",
                        label: "Apply (or direct a partner to apply) manual c-spine stabilization",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["Part of managing a head and/or spinal injury includes manual stabilization of the cervical spine before beginning your assessment, as movement may further injure the patient."],
                        feedbackOutOfOrder: ["The cervical spine needs to be manually supported before and after the application of a cervical collar. Manual support can be stopped after the CID is applied on the long backboard."],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "intv-spinal-immobilization-technique-cervical-collar",
                        label: "Apply an appropriately sized cervical collar",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["A cervical collar will help you maintain stability while you continue with your exam."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                ],
              },
              {
                id: "airway",
                label: "Assess the patient's airway",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["Be sure to assess the airway for patency and intact physical structures.", "You would have found this patient's airway to be partially obstructed with vomit and water, indicating the need for suctioning."],
                feedbackOutOfOrder: ["If your patient is conscious, you should examine the airway before breathing and circulation. If a patient is unconscious, you should assess the pulse first to evaluate for possible cardiac arrest, then go through your ABCs."],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: true,
                subActions: [
                      {
                        id: "airway-is-open",
                        label: "Check if the airway is patent",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["The first step of your airway assessment is to see if the patient's airway is open. If the patient is speaking normally, it is open. Signs like stridor or gasping may indicate an airway obstruction.", "This patient's airway was partially obstructed with vomit and water, which should have been suctioned."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "airway-has-intact-physical-structures",
                        label: "Check if the airway has intact physical structures",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["Though this patient did not have any facial trauma, be sure to look for injuries such as gun-shot wounds or facial fractures, as these may affect what basic or advanced airways you can use.  Avoid using a nasopharyngeal airway in patients with a suspected basilar skull fracture or nasal injury."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                ],
              },
              {
                id: "Manage a compromised airway",
                label: "Manage the patient’s compromised airway",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["This patient was unable to protect his airway, as evidenced by his being unconscious and having an obstructed airway. You should have managed this airway with suctioning, ventilating with a BVM, and ultimately intubating him."],
                feedbackOutOfOrder: ["You should identify any airway obstructions, including blood, vomiting, and secretions while checking the patency of your patient's airway. Any obstruction should be addressed at that time."],
                feedbackErrors: [""],
                examineIfAbsent: true,
                examineIfErrors: false,
                promptsIfAbsent: "didn't recognize that being unconscious was an indication for intubation; didn't recognize that the patient's airway was obstructed and needed suctioning",
                promptsIfErrors: "",
                subActionsList: true,
                subActions: [
                      {
                        id: "intv-open-airway-method-modified-jaw-thrust",
                        label: "Open the patient's airway with a jaw thrust maneuver",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["Opening the airway is a necessary precursor to inserting a basic adjunct.  The patient is at risk for a cervical spine injury, so opening the airway via head tilt can compromise spinal stability. Instead, you should use the jaw-thrust technique to open the airway while maintaining the cervical spine."],
                        feedbackOutOfOrder: ["You should have performed a jaw thrust maneuver before starting the secondary survey because all life-threatening conditions should be managed before starting the secondary survey."],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "intv-airway-patency-technique-suction-airway",
                        label: "Suction the patient's mouth using a Yankauer (rigid) or Long multi-use catheter",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["This patient had water and vomit in his airway, which should have been suctioned to establish a patent airway and allow you to insert a basic airway and, ultimately, to intubate this patient."],
                        feedbackOutOfOrder: ["Suctioning falls under 'A' in the ABCs. Airway obstructions are a potentially life threatening condition and require immediate intervention. Thus, debris in the airway or obstructions need to be identified and fixed during the primary assessment.", "In critical patients (especially those that are unconscious), the airway requires frequent reassessment. Be even more cautious when the patient has vomited recently or may lose consciousness very quickly.", "The patient's own tongue can be an obstruction, as well (when unconscious)."],
                        feedbackErrors: ["Both Yankauer (rigid) and Long multi-use catheters are appropriate for oral suctioning; however, Yankauer would be more effective for suctioning large volumes of blood, vomit, etc.  A soft tip suction catheter should be used for deep suctioning with the ET tube."],
                        examineIfAbsent: true,
                        examineIfErrors: false,
                        promptsIfAbsent: "didn't ensure the airway was clear; did not think the vomit or water needed to be suctioned prior to intubation",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "intv-position-patient", /* optional intervention */
                        label: "Position the patient",
                        type: ACTION_TYPES.OPT,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: ["You should have placed the patient in supine position before starting the secondary survey because all life-threatening conditions should be managed before starting the secondary survey."],
                        feedbackErrors: ["Placing the child on his left side would help to maintain his airway"],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "request-intervention-status-intv-airway-patency-technique-suction-airway",
                        label: "Reassess airway patency",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["After every intervention, it is vital to reassess the patient to observe the effect of that intervention. If suctioning is performed but gurgling still occurs on respirations, then further suctioning is required. Furthermore, if suctioning was performed and the airway remains clear, yet the patient continues to desaturate (SPO2), the provider should assess and think about other likely problems that caused the patient condition to worsen."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "intv-oro-or-naso-airway",
                        label: "Insert a basic airway adjunct",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["Remember: 'basic before advanced'. This patient is unconscious and therefore unable to maintain his airway (e.g. without an OPA, the tongue could obstruct the airway.)  Insert a basic airway adjunct to maintain airway patency and allow you to pre-oxygenate the patient prior to intubation."],
                        feedbackOutOfOrder: ["In the event of a potentially compromised airway (including patients who are unconscious), the patient needs to have an airway adjunct for airway support prior to the secondary survey."],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [
                          {
                            id: "intv-oropharyngeal-airway",
                            label: "Insert a basic airway adjunct",
                            type: ACTION_TYPES.REQ,
                            feedbackAbsent: ["Remember: 'basic before advanced'. This patient is unconscious and therefore unable to maintain his airway (e.g. without an OPA, the tongue could obstruct the airway.)  Insert a basic airway adjunct to maintain airway patency and allow you to pre-oxygenate the patient prior to intubation."],
                            feedbackOutOfOrder: ["In the event of a potentially compromised airway (including patients who are unconscious), the patient needs to have an airway adjunct for airway support prior to the secondary survey."],
                            examineIfAbsent: false,
                            examineIfErrors: false,
                            promptsIfAbsent: "",
                            promptsIfErrors: "",
                            subActionsList: false,
                            subActions: []
                          },
                          {
                            id: "intv-nasopharyngeal-airway",
                            label: "Insert a basic airway adjunct",
                            type: ACTION_TYPES.ALT,
                            alternativeToIntv: "intv-oropharyngeal-airway",
                            feedbackAbsent: ["Remember: 'basic before advanced'. This patient is unconscious and therefore unable to maintain his airway (e.g. without an OPA, the tongue could obstruct the airway.)  Insert a basic airway adjunct to maintain airway patency and allow you to pre-oxygenate the patient prior to intubation."],
                            feedbackOutOfOrder: ["In the event of a potentially compromised airway (including patients who are unconscious), the patient needs to have an airway adjunct for airway support prior to the secondary survey."],
                            examineIfAbsent: false,
                            examineIfErrors: false,
                            promptsIfAbsent: "",
                            promptsIfErrors: "",
                            subActionsList: false,
                            subActions: []
                          },
                        ],
                      },
                      {
                        id: "request-intv-status-NPA-or-OPA",
                        label: "Confirm that the patient accepted the airway adjunct",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["Remember to confirm that the patient accepted the airway adjunct"],
                        feedbackOutOfOrder: ["In the event of a potentially compromised airway (including patients who are unconscious), the patient needs to have an airway adjunct for airway support prior to the secondary survey."],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [
                          {
                            id: "request-intervention-status-intv-nasopharyngeal-airway",
                            label: "Confirm that the patient accepted the NPA",
                            type: ACTION_TYPES.REQ,
                            feedbackAbsent: ["Remember to confirm that the patient accepted the NPA"],
                            feedbackOutOfOrder: ["In the event of a potentially compromised airway (including patients who are unconscious), the patient needs to have an airway adjunct for airway support prior to the secondary survey."],
                            examineIfAbsent: false,
                            examineIfErrors: false,
                            promptsIfAbsent: "",
                            promptsIfErrors: "",
                            subActionsList: false,
                            subActions: []
                          },
                          {
                            id: "request-intervention-status-intv-oropharyngeal-airway",
                            label: "Confirm that the patient accepted the OPA",
                            type: ACTION_TYPES.ALT,
                            alternativeToIntv: "request-intervention-status-intv-nasopharyngeal-airway",
                            feedbackAbsent: ["Remember to confirm that the patient accepted the OPA"],
                            feedbackOutOfOrder: ["In the event of a potentially compromised airway (including patients who are unconscious), the patient needs to have an airway adjunct for airway support prior to the secondary survey."],
                            examineIfAbsent: false,
                            examineIfErrors: false,
                            promptsIfAbsent: "",
                            promptsIfErrors: "",
                            subActionsList: false,
                            subActions: []
                          },
                        ],
                      },
                      {
                        id: "intubate-patient", /* has children intv-preoxygenation and intv-oro-intubation-or-advanced-airway */
                        label: "Ultimately, intubate the patient",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["Ultimately, this patient should be intubated. This patient has a GCS score of 3; remember, GCS <8, intubate. Since he is unconscious, you need a reliable way to oxygenate and ventilate this patient. While BVM ventilation with an adjunct may be appropriate for a short transport, this near-drowning patient is at increased risk for aspiration, so securing an airway is the best course of action. Orotracheal intubation would be most appropriate because he lacks a gag reflex."], /* feedback taken from intv-oro-intubation-or-advanced-airway */
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["Inserting an appropriately sized endotracheal tube (ETT) is the most appropriate method of securing this patient's airway, since he lacks a gag reflex.", "Calculate the appropriate size ET tube for a child with the equation age/4 +1. This is the correct size for an uncuffed tube; go down by .5 for a cuffed tube. This child needed a 4.5 cuffed or 5.0 uncuffed tube.  The insertion depth for children is 3cm * (tube size), so about 15cm.", "If using a different advanced airway, reference the Broslow Tape for the acceptable size, but remember that Combitubes are not sized for children."], /* feedback taken from intv-oro-intubation-or-advanced-airway */
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: true,
                        subActions: [
                          {
                            id: "intv-preoxygenation",
                            label: "Pre-oxygenating the patient using a NRM, or a BVM alone or coupled with a nasal cannula.",
                            type: ACTION_TYPES.REQ,
                            feedbackAbsent: ["This patient has inadequate respirations and requires ventilatory support. Even if you identify immediately that you want to intubate this patient, he would benefit from starting with a bag-valve mask or NRM attached to 15LPM O2 to preoxygenate him prior to intubation. Alternatively, oxygenation with a nasal cannula could be coupled with BVM ventilation."],
                            feedbackOutOfOrder: ["Problems with ventilation and oxygenation are life threatening and should be addressed by oxygen administration and BVM ventilation before you move on to your secondary survey. Repeat your exam and check vitals to ensure your interventions have worked as you expected. If not, your patient may deteriorate as you continue on with your examination."],
                            feedbackErrors: ["Use a bag-valve mask or NRM attached to 15LPM O2 or greater to preoxygenate the patient prior to intubation. Alternatively, 25 LPM of O2 via nasal cannula while simultaneously performing BVM ventilations with high-flow oxygen could be used for pre-oxygenation."],
                            examineIfAbsent: true,
                            examineIfErrors: false,
                            promptsIfAbsent: "Didn't realize the patient needed ventilatory support; Didn't think the patient needed bagging before intubation",
                            promptsIfErrors: "",
                            subActionsList: false,
                            subActions: [
                              {
                                id: "intv-supplemental-oxygen-device-non-rebreather-mask",
                                label: "Ventilate the patient with non-rebreather mask",
                                type: ACTION_TYPES.REQ,
                                feedbackAbsent: ["This patient has inadequate respirations and requires ventilatory support. Even if you identify immediately that you want to intubate this patient, he would benefit from starting with a bag-valve mask or NRM attached to 15LPM O2 to preoxygenate him prior to intubation. Alternatively, oxygenation with a nasal cannula could be coupled with BVM ventilation."],
                                feedbackOutOfOrder: ["Problems with ventilation and oxygenation are life threatening and should be addressed by oxygen administration and BVM ventilation before you move on to your secondary survey. Repeat your exam and check vitals to ensure your interventions have worked as you expected. If not, your patient may deteriorate as you continue on with your examination."],
                                feedbackErrors: ["Use a bag-valve mask or NRM attached to 15LPM O2 or greater to preoxygenate the patient prior to intubation. Alternatively, 25 LPM of O2 via nasal cannula while simultaneously performing BVM ventilations with high-flow oxygen could be used for pre-oxygenation."],
                                examineIfAbsent: false,
                                examineIfErrors: false,
                                promptsIfAbsent: "",
                                promptsIfErrors: "",
                                subActionsList: false,
                                subActions: [],
                              },
                              {
                                id: "intv-ventilation-technique-bag-valve-mask",
                                label: "Ventilate the patient with bag valve mask",
                                type: ACTION_TYPES.ALT,
                                alternativeToIntv: "intv-supplemental-oxygen-device-non-rebreather-mask",
                                feedbackAbsent: ["This patient has inadequate respirations and requires ventilatory support. Even if you identify immediately that you want to intubate this patient, he would benefit from starting with a bag-valve or NRM mask attached to 15LPM O2 to preoxygenate him prior to intubation. Alternatively, oxygenation with a nasal cannula could be coupled with BVM ventilation."],
                                feedbackOutOfOrder: ["Problems with ventilation and oxygenation are life threatening and should be addressed by oxygen administration and BVM ventilation before you move on to your secondary survey. Repeat your exam and check vitals to ensure your interventions have worked as you expected. If not, your patient may deteriorate as you continue on with your examination."],
                                feedbackErrors: ["Use a bag-valve mask or NRM attached to 15LPM O2 or greater to preoxygenate the patient prior to intubation. Alternatively, 25 LPM of O2 via nasal cannula while simultaneously performing BVM ventilations with high-flow oxygen could be used for pre-oxygenation."],
                                examineIfAbsent: false,
                                examineIfErrors: false,
                                promptsIfAbsent: "",
                                promptsIfErrors: "",
                                subActionsList: false,
                                subActions: [],
                              },
                              {
                                id: "intv-bvm-and-nasal-cannula",
                                label: "Intubate the patient by pre-oxygenating them with a nasal cannula or BVM",
                                type: ACTION_TYPES.ALT,
                                alternativeToIntv: "intv-ventilation-technique-bag-valve-mask",
                                feedbackAbsent: ["This patient has inadequate respirations and requires ventilatory support. Even if you identify immediately that you want to intubate this patient, he would benefit from starting with a bag-valve or NRM mask attached to 15LPM O2 to preoxygenate him prior to intubation. Alternatively, oxygenation with a nasal cannula could be coupled with BVM ventilation."],
                                feedbackOutOfOrder: ["Problems with ventilation and oxygenation are life threatening and should be addressed by oxygen administration and BVM ventilation before you move on to your secondary survey. Repeat your exam and check vitals to ensure your interventions have worked as you expected. If not, your patient may deteriorate as you continue on with your examination."],
                                feedbackErrors: ["Use a bag-valve mask or NRM attached to 15LPM O2 or greater to preoxygenate the patient prior to intubation. Alternatively, 25 LPM of O2 via nasal cannula while simultaneously performing BVM ventilations with high-flow oxygen could be used for pre-oxygenation."],
                                examineIfAbsent: false,
                                examineIfErrors: false,
                                promptsIfAbsent: "",
                                promptsIfErrors: "",
                                subActionsList: false,
                                subActions: [
                                  {
                                    id: "intv-ventilation-technique-bag-valve-mask",
                                    label: "Ventilate the patient with bag valve mask",
                                    type: ACTION_TYPES.REQ,
                                    feedbackAbsent: ["This patient has inadequate respirations and requires ventilatory support. Even if you identify immediately that you want to intubate this patient, he would benefit from starting with a bag-valve mask attached to 15LPM O2 to preoxygenate him prior to intubation. Alternatively, oxygenation with a nasal cannula could be coupled with BVM ventilation."],
                                    feedbackOutOfOrder: ["Problems with ventilation and oxygenation are life threatening and should be addressed by oxygen administration and BVM ventilation before you move on to your secondary survey. Repeat your exam and check vitals to ensure your interventions have worked as you expected. If not, your patient may deteriorate as you continue on with your examination."],
                                    feedbackErrors: ["Use a bag-valve mask attached to 15LPM O2 or greater to preoxygenate the patient prior to intubation. Alternatively, 25 LPM of O2 via nasal cannula while simultaneously performing BVM ventilations with high-flow oxygen could be used for pre-oxygenation."],
                                    examineIfAbsent: false,
                                    examineIfErrors: false,
                                    promptsIfAbsent: "",
                                    promptsIfErrors: "",
                                    subActionsList: false,
                                    subActions: [],
                                  },
                                  {
                                    id: "intv-supplemental-oxygen-device-nasal-cannula",
                                    label: "Nasal cannula",
                                    type: ACTION_TYPES.REQ,
                                    feedbackAbsent: ["This patient has inadequate respirations and requires ventilatory support. Even if you identify immediately that you want to intubate this patient, he would benefit from starting with a bag-valve mask attached to 15LPM O2 to preoxygenate him prior to intubation. Alternatively, oxygenation with a nasal cannula could be coupled with BVM ventilation."],
                                    feedbackOutOfOrder: ["Problems with ventilation and oxygenation are life threatening and should be addressed by oxygen administration and BVM ventilation before you move on to your secondary survey. Repeat your exam and check vitals to ensure your interventions have worked as you expected. If not, your patient may deteriorate as you continue on with your examination."],
                                    feedbackErrors: ["Use a bag-valve mask attached to 15LPM O2 or greater to preoxygenate the patient prior to intubation. Alternatively, 25 LPM of O2 via nasal cannula while simultaneously performing BVM ventilations with high-flow oxygen could be used for pre-oxygenation."],
                                    examineIfAbsent: false,
                                    examineIfErrors: false,
                                    promptsIfAbsent: "",
                                    promptsIfErrors: "",
                                    subActionsList: false,
                                    subActions: [],
                                  },
                                ],
                              },
                            ],
                          },
                          {
                            id: "intv-oro-intubation-or-advanced-airway",
                            label: "Intubate the patient by placing an appropriately sized endotracheal tube or alternative/rescue airway",
                            type: ACTION_TYPES.REQ,
                            feedbackAbsent: ["Ultimately, this patient should be intubated. This patient has a GCS score of 3; remember, GCS <8, intubate. Since he is unconscious, you need a reliable way to oxygenate and ventilate this patient. While BVM ventilation with an adjunct may be appropriate for a short transport, this near-drowning patient is at increased risk for aspiration, so securing an airway is the best course of action. Orotracheal intubation would be most appropriate because he lacks a gag reflex."],
                            feedbackOutOfOrder: ["You should have performed orotracheal intubation or inserted an alternative/rescue airway before starting the secondary survey because all life-threatening conditions should be managed before starting the secondary survey."],
                            feedbackErrors: ["Inserting an appropriately sized endotracheal tube (ETT) is the most appropriate method of securing this patient's airway, since he lacks a gag reflex.  An advanced airway would be indicated if the airway cannot be secured with an ET tube due to difficulty intubating the patient.", "Calculate the appropriate size ET tube for a child with the equation age/4 +1. This is the correct size for an uncuffed tube; go down by .5 for a cuffed tube. This child needed a 4.5 cuffed or 5.0 uncuffed tube.  The insertion depth for children is 3cm * (tube size), so about 15cm.", "If using an advanced/rescue airway, reference the Broslow Tape for the acceptable size, but remember that Combitubes are not sized for children."],
                            examineIfAbsent: false,
                            examineIfErrors: true,
                            promptsIfAbsent: "",
                            promptsIfErrors: "Didn't recall how to calculate the size of an ET tube for a child; Didn't recall how to calculate the depth of an ET tube for a child",
                            subActionsList: false,
                            subActions: [
                              {
                                id: "intv-orotracheal-intubation",
                                label: "perform orotracheal intubation",
                                type: ACTION_TYPES.REQ,
                                feedbackAbsent: ["Ultimately, this patient should be intubated. This patient has a GCS score of 3; remember, GCS <8, intubate. Since he is unconscious, you need a reliable way to oxygenate and ventilate this patient. While BVM ventilation with an adjunct may be appropriate for a short transport, this near-drowning patient is at increased risk for aspiration, so securing an airway is the best course of action. Orotracheal intubation would be most appropriate because he lacks a gag reflex."],
                                feedbackOutOfOrder: ["You should have performed orotracheal intubation before starting the secondary survey because all life-threatening conditions should be managed before starting the secondary survey."],
                                feedbackErrors: ["Inserting an appropriately sized endotracheal tube (ETT) is the most appropriate method of securing this patient's airway, since he lacks a gag reflex.  An advanced airway would be indicated if the airway cannot be secured with an ET tube due to difficulty intubating the patient.", "Calculate the appropriate size ET tube for a child with the equation age/4 +1. This is the correct size for an uncuffed tube; go down by .5 for a cuffed tube. This child needed a 4.5 cuffed or 5.0 uncuffed tube.  The insertion depth for children is 3cm * (tube size), so about 15cm.", "If using an advanced/rescue airway, reference the Broslow Tape for the acceptable size, but remember that Combitubes are not sized for children."],
                                examineIfAbsent: false,
                                examineIfErrors: false,
                                promptsIfAbsent: "",
                                promptsIfErrors: "",
                                subActionsList: false,
                                subActions: [],
                              },
                              {
                                id: "intv-insert-advanced-airway",
                                label: "insert advanced airway",
                                type: ACTION_TYPES.ALT,
                                alternativeToIntv: "intv-orotracheal-intubation",
                                feedbackAbsent: ["Ultimately, this patient should be intubated. This patient has a GCS score of 3; remember, GCS <8, intubate. Since he is unconscious, you need a reliable way to oxygenate and ventilate this patient. While BVM ventilation with an adjunct may be appropriate for a short transport, this near-drowning patient is at increased risk for aspiration, so securing an airway is the best course of action. Orotracheal intubation would be most appropriate because he lacks a gag reflex."],
                                feedbackOutOfOrder: ["You should have inserted an advanced airway before starting the secondary survey because all life-threatening conditions should be managed before starting the secondary survey."],
                                feedbackErrors: ["Inserting an appropriately sized endotracheal tube (ETT) is the most appropriate method of securing this patient's airway, since he lacks a gag reflex.  An advanced airway would be indicated if the airway cannot be secured with an ET tube due to difficulty intubating the patient.", "Calculate the appropriate size ET tube for a child with the equation age/4 +1. This is the correct size for an uncuffed tube; go down by .5 for a cuffed tube. This child needed a 4.5 cuffed or 5.0 uncuffed tube.  The insertion depth for children is 3cm * (tube size), so about 15cm.", "If using an advanced/rescue airway, reference the Broslow Tape for the acceptable size, but remember that Combitubes are not sized for children."],
                                examineIfAbsent: false,
                                examineIfErrors: false,
                                promptsIfAbsent: "",
                                promptsIfErrors: "",
                                subActionsList: false,
                                subActions: [],
                              },
                              {
                                id: "intv-nasotracheal-intubation",
                                label: "nasotracheal intubation",
                                type: ACTION_TYPES.ALT,
                                alternativeToIntv: "intv-orotracheal-intubation",
                                feedbackAbsent: ["Ultimately, this patient should be intubated. This patient has a GCS score of 3; remember, GCS <8, intubate. Since he is unconscious, you need a reliable way to oxygenate and ventilate this patient. While BVM ventilation with an adjunct may be appropriate for a short transport, this near-drowning patient is at increased risk for aspiration, so securing an airway is the best course of action. Orotracheal intubation would be most appropriate because he lacks a gag reflex."],
                                feedbackOutOfOrder: ["You should have performed nasotracheal intubation before starting the secondary survey because all life-threatening conditions should be managed before starting the secondary survey."],
                                feedbackErrors: ["Inserting an appropriately sized endotracheal tube (ETT) is the most appropriate method of securing this patient's airway, since he lacks a gag reflex.  An advanced airway would be indicated if the airway cannot be secured with an ET tube due to difficulty intubating the patient.", "Calculate the appropriate size ET tube for a child with the equation age/4 +1. This is the correct size for an uncuffed tube; go down by .5 for a cuffed tube. This child needed a 4.5 cuffed or 5.0 uncuffed tube.  The insertion depth for children is 3cm * (tube size), so about 15cm.", "If using an advanced/rescue airway, reference the Broslow Tape for the acceptable size, but remember that Combitubes are not sized for children."],
                                examineIfAbsent: false,
                                examineIfErrors: false,
                                promptsIfAbsent: "",
                                promptsIfErrors: "",
                                subActionsList: false,
                                subActions: [],
                              }
                            ],
                          },
                        ],
                      },
                      {
                        id: "request-intervention-status-intv-orotracheal-intubation",
                        label: "Confirm tube placement by checking ETCO2 capnography, listening for bilateral lung sounds and absence of gastric sounds",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["Confirm tube placement by checking ETCO2 capnography, listening for bilateral lung sounds and absence of gastric sounds.  You should visualize the tube passing through the vocal cords as initial confirmation when performing the procedure."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "intv-contact-medical-command", /*optional intervention */
                        label: "contact medical command",
                        type: ACTION_TYPES.OPT,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "intv-open-airway-method-head-tilt", /*not indicated intervention */
                        label: "Head tilt chin lift",
                        type: ACTION_TYPES.NOTIND,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["A head-tilt chin lift is contraindicated for a patient with suspected c-spine injury as tilting the head could cause further damage to the neck. You should have used a modified jaw thrust to check this patient's airway."],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },                  
                      {
                        id: "intv-manual-finger-sweep", /* not indicated intervention */
                        label: "Manual (finger sweep)",
                        type: ACTION_TYPES.NOTIND,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["If there is a visible foreign body in the mouth, it should be removed. However, a blind finger sweep is contraindicated as it could lodge a foreign body deeper into the airway."],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "intv-magill-forceps-assisted", /* not indicated intervention */
                        label: "Magill forceps assisted removal",
                        type: ACTION_TYPES.NOTIND,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["If there is a visible foreign body in the mouth, it should be removed. However, there is no evidence of a foreign body in this patient's mouth."],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "intv-heimlich-maneuver", /* not indicated intervention */
                        label: "Heimlich Maneuver",
                        type: ACTION_TYPES.NOTIND,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["You have no evidence the patient is choking on an object, so the Heimlich maneuver is not appropriate at this time."],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "intv-back-blows-and-chest-thrusts", /* not indicated intervention */
                        label: "Back blows and chest thrusts",
                        type: ACTION_TYPES.NOTIND,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["You have no evidence the patient is choking on an object, so the Heimlich maneuver is not appropriate at this time."],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      /*{
                        id: "intv-nasotracheal-intubation", /* not indicated intervention 
                        label: "Nasotracheal intubation",
                        type: ACTION_TYPES.NOTIND,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["Nasotracheal intubation is only indicated for specific scenarios where access via the mouth is limited, such as profuse angioedema or trismus. It is a blind procedure and much more difficult than orotracheal intubation so in this patient who has a patent and intact oral airway, orotracheal intubation is preferred."],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },*/
                      {
                        id: "intv-rapid-sequence-intubation", /* not indicated intervention */
                        label: "Rapid-sequence intubation",
                        type: ACTION_TYPES.NOTIND,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["RSI is not in the paramedic's scope of practice in Pennsylvania"],
                        examineIfAbsent: false,
                        examineIfErrors: true,
                        promptsIfAbsent: "",
                        promptsIfErrors: "didn't know what type of intubation can be performed in pediatric patients in PA.",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "intv-sedation-assisted-intubation", /* not indicated intervention */
                        label: "Sedation-assisted intubation",
                        type: ACTION_TYPES.NOTIND,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["Sedation assisted intubation is contraindicated for pediatric patients in PA.  Since this patient is unconscious, orotracheal intubation would be the best option for securing the patient's airway."],
                        examineIfAbsent: false,
                        examineIfErrors: true,
                        promptsIfAbsent: "",
                        promptsIfErrors: "didn't know what type of intubation can be performed in pediatric patients in PA.",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "intv-needle-cricothyrotomy", /* not indicated intervention */
                        label: "Needle cricothyrotomy",
                        type: ACTION_TYPES.NOTIND,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["A cricothyrotomy is indicated when there is an upper airway obstruction that cannot be removed. This patient has a patent airway."],
                        examineIfAbsent: false,
                        examineIfErrors: true,
                        promptsIfAbsent: "",
                        promptsIfErrors: "thought the patient had an upper airway obstruction",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "intv-surgical-cricothyrotomy", /* not indicated intervention */
                        label: "Surgical cricothyrotomy",
                        type: ACTION_TYPES.NOTIND,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["A cricothyrotomy is indicated when there is an upper airway obstruction that cannot be removed. This patient has a patent airway."],
                        examineIfAbsent: false,
                        examineIfErrors: true,
                        promptsIfAbsent: "",
                        promptsIfErrors: "thought the patient had an upper airway obstruction",
                        subActionsList: false,
                        subActions: [],
                      },
                ],
              },
              {
                id: "breathing",
                label: "Assess the patient's breathing",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["After assessing the patient's airway, assess their breathing by listening for breath sounds and observing their respiratory rate, rhythm, and effort.", "You would have found bradypnea and heard gurgling.  This should have prompted you to apply BVM ventilation or other ventilatory support."],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: true,
                subActions: [
                    {
                        id: "breathing-checks-rate",
                        label: "rate",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["A significantly elevated or depressed respiratory rate can suggest impending respiratory failure.", "This patient's bradypnea suggests the need for ventilatory support, not just oxygenation."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "breathing-checks-rhythm",
                        label: "rhythm",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["Although not found in this case, a patient who is breathing irregularly needs ventilatory support."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "breathing-checks-quality",
                        label: "quality",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["Sounds like stridor, crackles, or wheezing will help you diagnose and manage your patient's respiratory problems.", "This patient had gurgling and bilateral rhonchi, suggesting the need for ventilatory support, not just oxygenation."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                ],
              },
              {
                id: "Manage inadequate breathing",
                label: "Manage inadequate breathing",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["The patient's respiratory rate is 6 with rhonchi appreciable on exam and no SpO2 capture. This patient needs both oxygen and ventilatory support, so O2 via nasal canula or ventimask would be inadequate."],
                feedbackOutOfOrder: ["Problems with ventilation and oxygenation are life threatening and should be addressed before you move on to your secondary survey. Repeat your exam and check vitals to ensure your interventions have worked as you expected. If not, your patient may deteriorate as you continue on with your examination."],
                feedbackErrors: [""],
                examineIfAbsent: true,
                examineIfErrors: false,
                promptsIfAbsent: "didn't realize that the patient's respiratory status was threatened ",
                promptsIfErrors: "",
                subActionsList: true,
                subActions: [
                    {
                        id: "intv-ventilation-technique-bag-valve-mask",
                        label: "ventilating the patient at a rate of 20 BPM, while titrating oxygen at a flow rate of 15 L/min to a target SpO2 >= 90%",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["The patient's respiratory rate is 6 with rhonchi appreciable on exam and no SpO2 capture. This patient needs both oxygen and ventilatory support, so O2 via nasal canula or Ventimask would be inadequate."],
                        feedbackOutOfOrder: ["Problems with ventilation and oxygenation are life threatening and should be addressed before you move on to your secondary survey. Repeat your exam and check vitals to ensure your interventions have worked as you expected. If not, your patient may deteriorate as you continue on with your examination."],
                        feedbackErrors: ["This patient should have been ventilated at a rate of 20 breaths per minute, which is the recommended rate for a child aged 2-12. When performing BVM ventilation, you use 15LPM O2 in order to both oxygenate and ventilate the patient to a target SpO2 >90%."],
                        examineIfAbsent:false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "reassess-AB",
                        label: "Check that airway/breathing interventions are working by requesting relevant vitals--in particular, BP, HR, and SpO2",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["BVM compliance is necessary during ventilations. This includes proper head positioning (sniffing the morning air position), proper positioning of the body (upper thorax elevated), and a proper BVM seal. If relevant vital signs (e.g., SPO2) do not improve or ventilations do not seem to be working, these factors should be reassessed. Other medical etiologies (choking, pneumothorax, vomitus in the airway, etc. should be considered) in such cases. If the patient continues to fail to improve, you may need to consider more invasive interventions, such as intubation."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [
                          {
                            id: "request-vitals-BP",
                            label: "vital BP",
                            type: ACTION_TYPES.REQ,
                            feedbackAbsent: ["BP is one of the relevant vitals to check if airway/breathing interventions are working"],
                            feedbackOutOfOrder: [""],
                            feedbackErrors: [""],
                            examineIfAbsent: false,
                            examineIfErrors: false,
                            promptsIfAbsent: "",
                            promptsIfErrors: "",
                            subActionsList: false,
                            subActions: [],
                          },
                          {
                            id: "request-vitals-Spo2",
                            label: "check Spo2",
                            type: ACTION_TYPES.REQ,
                            feedbackAbsent: ["Spo2 is one of the relevant vitals to check if airway/breathing interventions are working"],
                            feedbackOutOfOrder: [""],
                            feedbackErrors: [""],
                            examineIfAbsent: false,
                            examineIfErrors: false,
                            promptsIfAbsent: "",
                            promptsIfErrors: "",
                            subActionsList: false,
                            subActions: [],
                          },
                          {
                            id: "request-vitals-P",
                            label: "check P",
                            type: ACTION_TYPES.REQ,
                            feedbackAbsent: ["P is one of the relevant vitals to check if airway/breathing interventions are working"],
                            feedbackOutOfOrder: [""],
                            feedbackErrors: [""],
                            examineIfAbsent: false,
                            examineIfErrors: false,
                            promptsIfAbsent: "",
                            promptsIfErrors: "",
                            subActionsList: false,
                            subActions: [],
                          },
                        ],
                      },
                      /*{
                        id: "intv-supplemental-oxygen-device-non-rebreather-mask", // not indicated intervention
                        label: "Non-rebreather mask",
                        type: ACTION_TYPE.CONTRA,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["This patient was unresponsive with slow, gurgling breaths, indicating need for ventilation, not just oxygenation. O2 delivery via a non-rebreather mask would not be adequate in this case, as it assists with oxygenation but does not assist with ventilation."],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },*/
                      {
                        id: "intv-occlusive-dressing", /* not indicated intervention */
                        label: "Occlusive dressing",
                        type: ACTION_TYPES.NOTIND,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["The patient did not have a penetrating chest wound."],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "intv-pleural-decompression", /* not indicated intervention */
                        label: "Needle decompression",
                        type: ACTION_TYPES.NOTIND,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors:["The patient did not have a tension pneumothorax. Notice that the patient had equal lung sounds bilaterally and improved after intubation."],
                        examineIfAbsent: false,
                        examineIfErrors: true,
                        promptsIfAbsent: "",
                        promptsIfErrors: "thought the child had a tension pneumothorax",
                        subActionsList: false,
                        subActions: [],
                      },
                ],
              },
              
              {
                id: "intv-control-severe-bleeding-technique-direct-pressure", /* not indicated intervention */
                label: "Direct pressure",
                type: ACTION_TYPES.NOTIND,
                feedbackAbsent: [""],
                feedbackOutOfOrder: [""],
                feedbackErrors: ["The patient did not have significant bleeding."],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: [],
              },
              {
                id: "intv-control-severe-bleeding-technique-tourniquet", /* not indicated intervention */
                label: "Tourniquet",
                type: ACTION_TYPES.NOTIND,
                feedbackAbsent: [""],
                feedbackOutOfOrder: [""],
                feedbackErrors: ["The patient did not have significant bleeding."],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: [],
              },
              {
                id: "intv-control-severe-bleeding-technique-2nd-tourniquet", /* not indicated intervention */
                label: "Second tourniquet",
                type: ACTION_TYPES.NOTIND,
                feedbackAbsent: [""],
                feedbackOutOfOrder: [""],
                feedbackErrors: ["The patient did not have significant bleeding."],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: [],
              },
              {
                id: "intv-control-severe-bleeding-technique-pack-wound-with-gauze", /* not indicated intervention */
                label: "Pack wound with gauze or hemostatic gauze",
                type: ACTION_TYPES.NOTIND,
                feedbackAbsent: [""],
                feedbackOutOfOrder: [""],
                feedbackErrors: ["The patient did not have significant bleeding."],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: [],
              },
              {
                id: "intv-control-severe-bleeding-technique-pressure-bandage", /* not indicated intervention */
                label: "Pressure dressing",
                type: ACTION_TYPES.NOTIND,
                feedbackAbsent: [""],
                feedbackOutOfOrder: [""],
                feedbackErrors: ["The patient did not have significant bleeding."],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: [],
              },
              {
                id: "transport-decision",
                label: "Make appropriate transport decisions based on your findings so far",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["Your ABCs and evaluation for life threats and level of consciousness will dictate where you transfer your patient.", "This patient was critically ill so you should have decided to transport him to the level 1 pediatric trauma center."],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: true,
                subActions: [
                    {
                        id: "decision-is-load-and-go",
                        label: "Determine that this is a critical patient",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["This patient is unconscious and needs ventilatory support. This patient should be considered critical."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["This patient is unconscious and needs ventilatory support. This patient should be considered critical."],
                        examineIfAbsent: true,
                        examineIfErrors: false,
                        promptsIfAbsent: "didn't realize this was a critically ill patient; felt this patient should be further stabilized on site prior to transfer",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "intv-transport",
                        label: "Decide to transport this patient to a Level 1 pediatric trauma center by air ambulance",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["This patient requires a pediatric trauma center because of age and physiological criteria. The physiological criteria indicating that the patient requires this type of facility are respiratory rate of less than 10 and GCS <5 (not following commands)", "The level 1 pediatric trauma center is 16 minutes away by air in this scenario; transport to this facility would only be contraindicated by a transport time > 45 minutes."],
                        feedbackOutOfOrder: ["Initial transport decisions should be made following the 'Primary Survey' (ABCs) because that is when the provider decides whether the patient can be further stabilized on scene or taken directly to a hospital in a 'load and go' scenario. The secondary assessment is only performed on scene if the situation is NOT a 'load and go.' Making transport decisions early is especially important if you are calling for an air ambulance to account for the time it takes for them to arrive."],
                        feedbackErrors: ["This patient requires a pediatric trauma center because of age and physiological criteria. The physiological criteria indicating that the patient requires this type of facility are respiratory rate of less than 10 and GCS <5 (not following commands)", "The level 1 pediatric trauma center is 16 minutes away by air in this scenario; transport to this facility would only be contraindicated by a transport time > 45 minutes."],
                        examineIfAbsent: false,
                        examineIfErrors: true,
                        promptsIfAbsent: "",
                        promptsIfErrors: "did not realize patient requires higher level of service than level II adult facility",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "intv-call-for-air-ambulance",
                        label: "Call for an air ambulance",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["This patient requires a pediatric trauma center, due to physiological criteria. Given the time required for an air ambulance to arrive, you should call for one as soon after you decide to transport the patient by helicopter as possible, after completing your Primary Survey."],
                        feedbackOutOfOrder: ["Call for an air ambulance before beginning your secondary survey to account for the time it takes for them to arrive."],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "intv-walk-patient-to-ambulance",
                        label: "walked the patient to the stretcher",
                        type: ACTION_TYPES.NOTIND,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["This patient is unconscious"],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                ],
              },
              {
                id: "trauma-expose",
                label: "Trauma expose the patient",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["Exposure is necessary to properly inspect the patient for injuries. Only the area being inspected should be uncovered and then recovered to prevent heat loss and hypothermia."],
                feedbackOutOfOrder: ["It is important to expose your patient to ensure that your secondary survey can be performed efficiently -- especially in trauma scenarios where there may be more unknown injuries. Exposing the patient allows the provider to more carefully examine the patient during assessment."],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: [{
                  id: "exposes-body-parts",
                  label: "Exposed (and recovered) anatomical areas as necessary",
                  type: ACTION_TYPES.REQ,
                  feedbackAbsent: ["Exposure is necessary to properly inspect the patient for injuries. Only the area being inspected should be uncovered and then recovered to prevent heat loss and hypothermia."],
                  feedbackOutOfOrder: ["It is important to expose your patient to ensure that your secondary survey can be performed efficiently -- especially in trauma scenarios where there may be more unknown injuries. Exposing the patient allows the provider to more carefully examine the patient during assessment."],
                  feedbackErrors: [""],
                  examineIfAbsent: false,
                  examineIfErrors: false,
                  promptsIfAbsent: "",
                  promptsIfErrors: "",
                  subActionsList: false,
                  subActions: [],
              },],
              },
          ],
        },
        {
          id: "history-taking",
          label: "Obtain or direct a partner to obtain a patient history",
          type: ACTION_TYPES.REQ,
          feedbackAbsent: ["Vital signs will help you decide how to manage your patient, as will a history. Even if you cannot obtain a history from the patient directly, you should still try to get as much information as you can (e.g., from a bystander)."],
          feedbackOutOfOrder: ["You should have obtained a set of vitals or directed a partner to do so before beginning your Secondary Survey. A patient's vital signs may prompt you to repeat parts of your Primary Assessment before moving onto the Secondary Survey."],
          feedbackErrors: [""],
          examineIfAbsent: false,
          examineIfErrors: false,
          promptsIfAbsent: "",
          promptsIfErrors: "",
          subActionsList: true,
          subActions: [
            {
                id: "required-action-obtains-vitals",
                label: "Baseline vital signs",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["Baseline vitals are critical; they will help you assess, manage, and continue to monitor your patient.", "In this scenario, you would have found that this patient was bradycardic, bradypnic, hypoxic, and hypotensive, alerting you to a critical patient who needed prompt intervention."],
                feedbackOutOfOrder: [""],
                feedbackErrors: ["Baseline vitals are critical; they will help you assess, manage, and continue to monitor your patient. At the minimum, you should have obtained BP, P, R, and SpO2."],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: [],
              },
              {
                id: "required-action-attempt-obtain-sample",
                label: "SAMPLE (if possible)",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["You should have taken an efficient but thorough SAMPLE history from the baby sitter: Symptoms, Allergies, Medications, Past medical history, Last oral intake, and preceding Events. You would have learned that the child was left unattended on a floating mattress in the pool for 5 minutes, found underwater, and then pulled out of the pool"],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: [],
              },
              {
                id: "required-action-obtain-OPQRST",
                label: "OPQRST (if possible)",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["Assess the patient's pain: onset, provocation/palliation, quality, region/radiation, severity, and time. If the patient cannot tell you his or herself, you should ask a family member or other bystander for whatever information they may have."],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: [],
              },
          ],
        },
        {
          id: "secondary-assessment",
          label: "Perform a Secondary Survey (rapid trauma assessment)",
          type: ACTION_TYPES.REQ,
          feedbackAbsent: ["The Secondary Survey is a thorough exam of your patient to ensure you have a complete understanding of what is going on and how you need to manage your patient."],
          feedbackOutOfOrder: [""],
          feedbackErrors: [""],
          examineIfAbsent: false,
          examineIfErrors: false,
          promptsIfAbsent: "",
          promptsIfErrors: "",
          subActionsList: true,
          subActions: [
            {
                id: "head",
                label: "examine the patient's head",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: [""],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: true,
                subActions: [
                    {
                        id: "assessment-head-injury",
                        label: "Check head for injury",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "assessment-inspects-facial-bones",
                        label: "Inspect and palpate the facial bones",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["Inspecting the face for bruising and palpating for facial deformity may indicate facial fractures."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "inspects-eyes",
                        label: "Inspect the eyes",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["Checking for PERRLA would have revealed that the patient's pupils were equal but sluggish, suggesting possible intracranial injury and a critically ill patient who needed rapid transfer to a trauma center.", "Bruising around the eyes may be caused by basilar skull fracture. Fortunately, this aspect of the exam was normal in this patient."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: true,
                        subActions: [ 
                          {
                            id: "inspects-eyes-PERRLA",
                            label: "for PERRLA",
                            type: ACTION_TYPES.REQ,
                            feedbackAbsent: ["Checking for PERRLA would have revealed that the patient's pupils were equal but sluggish, suggesting possible intracranial injury and a critically ill patient who needed rapid transfer to a trauma center."],
                            feedbackOutOfOrder: [""],
                            feedbackErrors: [""],
                            examineIfAbsent: false,
                            examineIfErrors: false,
                            promptsIfAbsent: "",
                            promptsIfErrors: "",
                            subActionsList: false,
                            subActions: []
                          },
                          {
                            id: "inspects-eyes-racoon",
                            label: "for bruising ('raccoon eyes')",
                            type: ACTION_TYPES.REQ,
                            feedbackAbsent: ["This aspect of the exam was normal in this patient but bruising around the eyes may be caused by basilar skull fracture."],
                            feedbackOutOfOrder: [""],
                            feedbackErrors: [""],
                            examineIfAbsent: false,
                            examineIfErrors: false,
                            promptsIfAbsent: "",
                            promptsIfErrors: "",
                            subActionsList: false,
                            subActions: []
                          }
                        ],
                      },
                      {
                        id: "inspects-ears",
                        label: "Inspect the ears for blood, other fluids, and bruising (\"battle signs\")",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["Although not found in this case, bleeding or CF from the ear or bruising behind the ear may be indicative of a basilar skull fracture (Battle's sign)."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [
                          {id: "inspects-ears-blood",
                           feedbackAbsent: ["Although not found in this case, bleeding or CF from the ear may be indicative of a basilar skull fracture."],
                           feedbackOutOfOrder: [""],
                           feedbackErrors: [""],
                           examineIfAbsent: false,
                           examineIfErrors: false,
                           promptsIfAbsent: "",
                           promptsIfErrors: "",
                           subActionsList: false,
                           subActions: []
                          },
                          {id: "inspects-ears-bruising",
                           feedbackAbsent: ["Although not found in this case, bruising behind the ear suggests a basilar skull fracture (Battle's sign)."],
                           feedbackOutOfOrder: [""],
                           feedbackErrors: [""],
                           examineIfAbsent: false,
                           examineIfErrors: false,
                           promptsIfAbsent: "",
                           promptsIfErrors: "",
                           subActionsList: false,
                           subActions: []
                          }
                        ],
                      },
                      {
                        id: "inspects-nose",
                        label: "Inspect the nose for blood and other fluids",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["This patient had no drainage from the nose but consider CSF leak from a basilar skull fracture if you see clear drainage."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "inspects-mouth",
                        label: "Inspect the mouth for blood, other secretions, and obstructions",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["During your primary survey, you should have noticed water and vomit in the patient's mouth; you should have suctioned the patient's airway.  If you had done this, at this stage the patient's airway would be clear of secretions.", "Additionally, any visualized foreign bodies in the mouth should be removed. A blind finger sweep is never indicated, as you could lodge a foreign body deeper into the airway. Besides the vomit and water you suctioned, there were no obstructions in this patient’s mouth."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                ],
              },
              {
                id: "neck",
                label: "examine the patient's neck",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: [""],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: true,
                subActions: [
                    {
                        id: "assessment-neck-injury",
                        label: "injury",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "assessment-tracheal-deviation",
                        label: "tracheal deviation",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["This patient's neck exam was normal, but remember, tracheal deviation suggests unequal intrathoracic pressure (for example, a pneumothorax). The trachea will be deviated away from the side with increased pressure."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "assessment-JVD",
                        label: "jugular venous distension (JVD)",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["This patient's neck exam was normal, but remember, JVD suggests that the heart is not adequately pushing out blood. Consider obstructive processes like a tension pneumothorax or hemothorax."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "assessment-stepoffs",
                        label: "step-offs",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["This patient's neck exam was normal, but remember, a palpable step-off suggests a fracture."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                ],
              },
              {
                id: "chest",
                label: "examine the patient's chest",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: [""],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: true,
                subActions: [
                    {
                        id: "assessment-check-breath-sounds",
                        label: "Listen for breath sounds",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["The presence and quality of breath sounds can clue you into underlying problems (e.g., pneumothorax, asthma, fluid build-up in the lungs).", "This patient had rales and crackles, suggesting fluid in the alveoli and interstitium of the lungs and possible pulmonary edema."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                       },
                       {
                        id: "assessment-inspect-shoulders",
                        label: "Inspect and palpate shoulders",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["Inspecting the shoulders for discoloration symmetry or deformity may identify dislocation or fracture of the shoulder (clavicle, humerus and scapula).  This patient's shoulders were normal."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "assessment-inspect-clavicles",
                        label: "Inspect and palpates clavicles",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["The clavicles should be inspected for any evidence of fractures. Though rare, clavicle fractures can cause a pneumothorax. This patient’s clavicles were normal."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "inspects-palpates-anterior-thorax",
                        label: "Inspect and palpate anterior thorax for injury, crepitation, and paradoxial motion or flail segments",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["If the patient had had anterior thorax and rib crepitation, you should suspect air outside of the lungs, which would raise your suspicion for a pneumothorax.", "The patient had symmetric movement of his chest. Recall that paradoxical chest wall motion suggests flail chest, where multiple rib fractures lead to an unstable portion of the chest wall."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [
                          {
                            id: "inspects-chest-injury",
                            feedbackAbsent: [""], /* no feedback on excel sheet */
                            feedbackOutOfOrder: [""],
                            feedbackErrors: [""],
                            examineIfAbsent: false,
                            examineIfErrors: false,
                            promptsIfAbsent: "",
                            promptsIfErrors: "",
                            subActionsList: false,
                            subActions: []
                          },
                          {
                            id: "inspects-palpates-anterior-thorax-crepitation",
                            feedbackAbsent: ["If the patient had had anterior thorax and rib crepitation, you should suspect air outside of the lungs, which would raise your suspicion for a pneumothorax."],
                            feedbackOutOfOrder: [""],
                            feedbackErrors: [""],
                            examineIfAbsent: false,
                            examineIfErrors: false,
                            promptsIfAbsent: "",
                            promptsIfErrors: "",
                            subActionsList: false,
                            subActions: []
                          },
                          {
                            id: "inspects-anterior-thorax-paradoxical-motion",
                            feedbackAbsent: ["The patient had symmetric movement of his chest. Recall that paradoxical chest wall motion suggests flail chest, where multiple rib fractures lead to an unstable portion of the chest wall."],
                            feedbackOutOfOrder: [""],
                            feedbackErrors: [""],
                            examineIfAbsent: false,
                            examineIfErrors: false,
                            promptsIfAbsent: "",
                            promptsIfErrors: "",
                            subActionsList: false,
                            subActions: []
                          },
                        ],
                      },
                ],
              },
              {
                id: "abdomen-pelvis",
                label: "examine the patient's abdomen",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["A distended or rigit abdomen suggests trauma and internal bleeding. Fortunately, the patient's abdominal exam was normal."],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: true,
                subActions: [
                    {
                        id: "inspects-abdomen-injury",
                        label: " inspect and palpate for injury",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: [""], /* no feedback on excel sheet */
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "inspects-palpates-abdomen-distension",
                        label: "inspect and palpate for distension",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["A distended abdomen suggests trauma and internal bleeding. Fortunately, the patient's abdominal exam was normal."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "inspects-palpates-abdomen-rigidity",
                        label: "inspect and palpate for rigidity",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["A rigid abdomen suggests trauma and internal bleeding. Fortunately, the patient's abdominal exam was normal."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                ],
              },
              {
                id: "assess-pelvis",
                label: "examine the patient's pelvis",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["Be sure to do a thorough exam of the pelvis as the pelvis can be a source of life-threatening bleeding. The presence of blood or other fluids may indicate urethral, vaginal or rectal injury. Fortunately, your exam of this patient’s pelvis and genitalia would have been normal."],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: true,
                subActions: [
                    {
                        id: "assessment-checks-pelvis",
                        label: "Inspect and compress the pelvis",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["Be sure to do a thorough exam of the pelvis as the pelvis can be a source of life-threatening bleeding. Fortunately, the patient's pelvis exam was normal."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "inspects-genitalia-perineum-blood",
                        label: "Inspect the genitalia/perineum for blood and other fluids",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["The presence of blood or other fluids may indicate urethral, vaginal or rectal injury.", "Your exam of this patient’s pelvis and genitalia would have been normal."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                ],
              },
              {
                id: "extremities",
                label: "examine the patient's upper and lower extremities",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: [""],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: true,
                subActions: [
                    {
                        id: "inspects-extremities-injury",
                        label: "Inspect and palpate each extremity for injury",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: [""], /* no feedback in excel sheet */
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [
                          { id: "inspects-right-arm-injury",
                            subActions: []},
                           {id: "inspects-left-arm-injury",
                            subActions: []},
                           {id: "inspects-right-leg-injury",
                            subActions: []},
                           {id: "inspects-left-leg-injury",
                            subActions: []},
                        ],
                      },
                      {
                        id: "inspects-extremities-pulse",
                        label: "Inspect and palpate each extremity for pulse",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["If you had checked this patient's extremity pulses, you would have noticed weak extremity pulses, due to inadequate cardiac output."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [
                          { id: "inspects-right-arm-pulse",
                            feedbackAbsent: ["If you had checked this patient's extremity pulses, you would have noticed weak extremity pulses, due to inadequate cardiac output."],
                            subActions: []},
                           {id: "inspects-left-arm-pulse",
                            feedbackAbsent: ["If you had checked this patient's extremity pulses, you would have noticed weak extremity pulses, due to inadequate cardiac output."],
                            subActions: []},
                           {id: "inspects-right-leg-pulse",
                            feedbackAbsent: ["If you had checked this patient's extremity pulses, you would have noticed weak extremity pulses, due to inadequate cardiac output."],
                            subActions: []},
                           {id: "inspects-left-leg-pulse",
                            feedbackAbsent: ["If you had checked this patient's extremity pulses, you would have noticed weak extremity pulses, due to inadequate cardiac output."],
                            subActions: []},
                        ],
                      },
                      {
                        id: "inspects-extremities-motor-function",
                        label: "Inspect and palpate each extremity for motor function",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["Generally, diminished or absent mobility may suggest spinal cord or other nervous system injury, but you were not able to perform a motor assessment in this unconscious patient."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [
                          { id: "inspects-right-arm-motor-ability",
                            feedbackAbsent: ["Generally, diminished or absent mobility may suggest spinal cord or other nervous system injury, but you were not able to perform a motor assessment in this unconscious patient."],
                            subActions: []},
                           {id: "inspects-left-arm-motor-ability",
                            feedbackAbsent: ["Generally, diminished or absent mobility may suggest spinal cord or other nervous system injury, but you were not able to perform a motor assessment in this unconscious patient."],
                            subActions: []},
                           {id: "inspects-right-leg-motor-ability",
                            feedbackAbsent: ["Generally, diminished or absent mobility may suggest spinal cord or other nervous system injury, but you were not able to perform a motor assessment in this unconscious patient."],
                            subActions: []},
                           {id: "inspects-left-leg-motor-ability",
                            feedbackAbsent: ["Generally, diminished or absent mobility may suggest spinal cord or other nervous system injury, but you were not able to perform a motor assessment in this unconscious patient."],
                            subActions: []},
                        ],
                      },
                      {
                        id: "inspects-extremities-sensory-function",
                        label: "Inspect and palpate each extremity for sensory function",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["Generally, diminished or absent sensation may suggest spinal cord or other nervous system injury, but you were not able to perform a motor assessment in this unconscious patient."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [
                          { id: "inspects-right-arm-sensory-function",
                            feedbackAbsent: ["Generally, diminished or absent sensation may suggest spinal cord or other nervous system injury, but you were not able to perform a motor assessment in this unconscious patient."],
                            subActions: []},
                           {id: "inspects-left-arm-sensory-function",
                            feedbackAbsent: ["Generally, diminished or absent sensation may suggest spinal cord or other nervous system injury, but you were not able to perform a motor assessment in this unconscious patient."],
                            subActions: []},
                           {id: "inspects-right-leg-sensory-function",
                            feedbackAbsent: ["Generally, diminished or absent sensation may suggest spinal cord or other nervous system injury, but you were not able to perform a motor assessment in this unconscious patient."],
                            subActions: []},
                           {id: "inspects-left-leg-sensory-function",
                            feedbackAbsent: ["Generally, diminished or absent sensation may suggest spinal cord or other nervous system injury, but you were not able to perform a motor assessment in this unconscious patient."],
                            subActions: []},
                        ],
                      }, 
                ],
              },
              {
                id: "posterior",
                label: "inspect and palpate the posterior thorax, lumbar, and buttocks for injury and step-offs",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["You will need a partner to help you roll the patient safely in order to do a thorough exam of the posterior thorax, lumbar, and buttocks for injury and step-offs. A palpable step-off, which the patient did not have, would suggest a fracture."],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: [
                    { id: "inspects-posterior-other-injury",
                      feedbackAbsent: [""], /* no feedback in excel sheet */
                      feedbackOutOfOrder: [""],
                      feedbackErrors: [""],
                      examineIfAbsent: false,
                      examineIfErrors: false,
                      promptsIfAbsent: "",
                      promptsIfErrors: "",
                      subActionsList: false,
                      subActions: []
                    },
                    {
                      id: "inspects-posterior-step-offs",
                      feedbackAbsent: ["A palpable step-off, which the patient did not have, would suggest a fracture."],
                      feedbackOutOfOrder: [""],
                      feedbackErrors: [""],
                      examineIfAbsent: false,
                      examineIfErrors: false,
                      promptsIfAbsent: "",
                      promptsIfErrors: "",
                      subActionsList: false,
                      subActions: []
                    },
                ],
              },
          ],
        },
        {
          id: "ongoing-mgmt-plan",
          label: "Continue to provide adequate prehospital care until arriving at the receiving facility",
          type: ACTION_TYPES.REQ,
          feedbackAbsent: [""],
          feedbackOutOfOrder: [""],
          feedbackErrors: ["How to transfer your patient to the stretcher depends on your level of concern for spinal injury but you should do so with minimal movement to the spine", "Given the low concern for further spinal injury (i.e. the patient was unlikely to have been diving), you could carefully place him directly on the stretcher, with manual c-spine stabilization and a c-collar in place. Alternately, you could use a backboard, scoop stretcher, vacuum mattress, or other device which could be removed after placing the patient on the stretcher.", "Supine position will be required for this patient as he is intubated and in order to properly position the airway."],
          examineIfAbsent: false,
          examineIfErrors: false,
          promptsIfAbsent: "",
          promptsIfErrors: "",
          subActionsList: true,
          subActions: [
            {
                id: "intv-place-on-stretcher-OR-immob-device",
                label: "Transfer the patient directly to the stretcher or by first placing him on an immobilization or transfer device.",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["How to transfer your patient to the stretcher depends on your level of concern for spinal injury but you should do so with minimal movement to the spine", "Given the low concern for further spinal injury (i.e. the patient was unlikely to have been diving), you could carefully place him directly on the stretcher, with manual c-spine stabilization and a c-collar in place. Alternately, you could use a backboard, scoop stretcher, vacuum mattress, or other device which could be removed after placing the patient on the stretcher.", "Supine position will be required for this patient as he is intubated and in order to properly position the airway."],
                feedbackOutOfOrder: [""],
                feedbackErrors: ["How to transfer your patient to the stretcher depends on your level of concern for spinal injury but you should do so with minimal movement to the spine", "Given the low concern for further spinal injury (i.e. the patient was unlikely to have been diving), you could carefully place him directly on the stretcher, with manual c-spine stabilization and a c-collar in place. Alternately, you could use a backboard, scoop stretcher, vacuum mattress, or other device which could be removed after placing the patient on the stretcher.", "Supine position will be required for this patient as he is intubated and in order to properly position the airway."],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: [
                  {
                    id: "intv-place-directly-on-stretcher",
                    label: "Transfer the patient directly to a stretcher",
                    type: ACTION_TYPES.REQ,
                    feedbackAbsent: ["How to transfer your patient to the stretcher depends on your level of concern for spinal injury but you should do so with minimal movement to the spine", "Given the low concern for further spinal injury (i.e. the patient was unlikely to have been diving), you could carefully place him directly on the stretcher, with manual c-spine stabilization and a c-collar in place. Alternately, you could use a backboard, scoop stretcher, vacuum mattress, or other device which could be removed after placing the patient on the stretcher.", "Supine position will be required for this patient as he is intubated and in order to properly position the airway."],
                    feedbackOutOfOrder: [""],
                    feedbackErrors: ["How to transfer your patient to the stretcher depends on your level of concern for spinal injury but you should do so with minimal movement to the spine", "Given the low concern for further spinal injury (i.e. the patient was unlikely to have been diving), you could carefully place him directly on the stretcher, with manual c-spine stabilization and a c-collar in place. Alternately, you could use a backboard, scoop stretcher, vacuum mattress, or other device which could be removed after placing the patient on the stretcher.", "Supine position will be required for this patient as he is intubated and in order to properly position the airway."],
                    examineIfAbsent: false,
                    examineIfErrors: false,
                    promptsIfAbsent: "",
                    promptsIfErrors: "",
                    subActionsList: false,
                    subActions: [],
                  },
                  {
                    id: "intv-place-on-immobilization-device",
                    label: "Transfer patient by first placing him on immobilization device",
                    type: ACTION_TYPES.ALT,
                    alternativeToIntv: "intv-place-directly-on-stretcher",
                    feedbackAbsent: ["How to transfer your patient to the stretcher depends on your level of concern for spinal injury but you should do so with minimal movement to the spine", "Given the low concern for further spinal injury (i.e. the patient was unlikely to have been diving), you could carefully place him directly on the stretcher, with manual c-spine stabilization and a c-collar in place. Alternately, you could use a backboard, scoop stretcher, vacuum mattress, or other device which could be removed after placing the patient on the stretcher.", "Supine position will be required for this patient as he is intubated and in order to properly position the airway."],
                    feedbackOutOfOrder: [""],
                    feedbackErrors: ["How to transfer your patient to the stretcher depends on your level of concern for spinal injury but you should do so with minimal movement to the spine", "Given the low concern for further spinal injury (i.e. the patient was unlikely to have been diving), you could carefully place him directly on the stretcher, with manual c-spine stabilization and a c-collar in place. Alternately, you could use a backboard, scoop stretcher, vacuum mattress, or other device which could be removed after placing the patient on the stretcher.", "Supine position will be required for this patient as he is intubated and in order to properly position the airway."],
                    examineIfAbsent: false,
                    examineIfErrors: false,
                    promptsIfAbsent: "",
                    promptsIfErrors: "",
                    subActionsList: false,
                    subActions: [],
                  },
                ],
              },
              {
                id: "intv-spinal-immobilization-technique-attach-cid",
                label: "Attach cervical immobilization device (CID)",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["Since you should have placed a collar on this patient due to potential head or spinal injury, you should have applied padding (e.g., CID blocks) to maintain stabilization during extrication and transport to the trauma center."],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: [],
              },
              {
                id: "intv-spinal-immobilization-technique-transfer-to-device", /* optional intervention */
                label: "Transfer patient to immobilization device",
                type: ACTION_TYPES.OPT,
                feedbackAbsent: [""],
                feedbackOutOfOrder: [""],
                feedbackErrors: ["This near-drowning patient who had an unwitnessed event needs cervical spine stabilization and a cervical collar. However, further spinal immobilization is optional, given the low concern for further spinal injury (i.e. the patient was unlikely to have been diving)."],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: [],
              },
              {
                id: "Reassess the patient",
                label: "Reassess the patient",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["It is important to reassess your patient after every intervention and every clinical change. This includes repeating the primary survey, secondary survey, and vital signs. A critically ill patient should be reassessed more frequently to ensure he or she is still stable--approximately every 5 minutes."],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: true,
                subActions: [
                  {
                    id: "reassess-vitals-plus-frequency",
                    label: "Recheck vital signs every 5 minutes",
                    type: ACTION_TYPES.REQ,
                    feedbackAbsent: ["Vitals should be reassessed after every intervention and every clinical change, and a critically ill patient should be reassessed every 5 minutes since their condition could deteriorate rapidly. In particular, in this scenario, you should have reassessed BP, P, R, and SpO2 to ensure that the patient’s airway and breathing were adequately managed."],
                    feedbackOutOfOrder: [""],
                    feedbackErrors: ["Vitals should be reassessed after every intervention and every clinical change, and a critically ill patient should be reassessed every 5 minutes since their condition could deteriorate rapidly. In particular, in this scenario, you should have reassessed BP, P, R, and SpO2 to ensure that the patient’s airway and breathing were adequately managed."],
                    examineIfAbsent: false,
                    examineIfErrors: false,
                    promptsIfAbsent: "",
                    promptsIfErrors: "",
                    subActionsList: false,
                    subActions: [
                      {
                        id: "reassess-vital-signs",
                        label: "Recheck vital signs every 5 minutes",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["Vitals should be reassessed after every intervention and every clinical change, and a critically ill patient should be reassessed more frequently to ensure he or she is still stable. In particular, in this scenario, you should have reassessed BP, P, R, and SpO2 to ensure that the patient’s airway and breathing were adequately managed."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["In this scenario, you should have reassessed BP, P, R, and SpO2 to ensure that the patient’s airway and breathing were adequately managed."],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [], 
                      },
                      {
                        id: "decision-vitals-frequency",
                        label: "decision vitals frequency",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["Vitals should be reassessed after every intervention and every clinical change, and a critically ill patient should be reassessed every 5 minutes since their condition could deteriorate rapidly."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["Vitals should be reassessed after every intervention and every clinical change, and a critically ill patient should be reassessed every 5 minutes since their condition could deteriorate rapidly."],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [], 
                      },
                    ],
                  },
                      {
                        id: "repeat-primary-survey",
                        label: "Repeat the Primary Survey",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["It is important to reassess your patient after every intervention and every clinical change, which includes the primary survey. For example, a patient with a neck injury whose breathing was initially non-labored may develop stridor as the airway becomes swollen."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "repeat-secondary-survey",
                        label: "Repeat the Secondary Survey",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["It is important to reassess your patient after every intervention and every clinical change, which includes your secondary survey. For example, a patient may lose their pulses if you initially failed to notice and treat a source of hemorrhage."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                ],
              },
              {
                id: "intv-control-shock-technique-keep-patient-warm", /* Collin: can you list vertically the items on the list in the field "label"? */
                label: "Keep the patient warm, using some of the following methods: (1) apply heat packs to the patient's groin or armpits, (2) remove wet clothing, (3) cover the patient with blankets, (4) turn the heat up in the ambulance.",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["It is important to keep this patient warm because hypothermia can cause additional problems or worsening of the patient's current problems, including hypotension, arrhythmias, or even cardiac arrest."],
                feedbackOutOfOrder: [""],
                feedbackErrors: ["You could keep the patient warm by removing his wet swimsuit, applying blankets, applying heat packs to his armpits or groin, and turning up the heat in the air ambulance."],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: []
              },
              {
                id: "intv-contact-receiving-facility",
                label: "Assist the air crew with giving a patient report to the receiving facility and ETA",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["The hospital requires notification for all incoming patients 5 -15 prior to arrival, which is especially important for critical patients as the receiving team needs time to prepare equipment and space.  During patient handover to the flight crew, you should relay the information that they need to convey to the receiving facility about the patient."],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: []
              },
              {
                id: "intv-establish-iv",
                label: "Establish at least one large bore IV en route",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["All trauma patients should have at least one, ideally two, large bore IVs placed, even if they do not require fluid resuscitation. With pediatric patients, this will likely be 18-, 20-, or 22- gauge IVs."],
                feedbackOutOfOrder: [""],
                feedbackErrors: ["With a critical patient, you should ideally establish two large bore IVs while en route to the receiving facility, so as not to delay transfer to definitive care.  With pediatric patients, this will likely be 18-, 20-, or 22- gauge IVs."],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: []
              },
              {
                id: "intv-control-shock-technique-administer-iv-boluses", /* not indicated intervention */
                label: "IV fluid resuscitation",
                type: ACTION_TYPES.NOTIND,
                feedbackAbsent: [""],
                feedbackOutOfOrder: [""],
                feedbackErrors: ["While it is true the patient was hypotensive, the reason for his hypotension was his respiratory failure, not hypovolemia. Appropriate management of the patient's airway and breathing would have improved his hypotension without the need for IV fluids. In fact, you should be conservative about fluid resuscitation in a near drowning patient because pulmonary edema would further hinder the patient's respiratory status. In general, when administering fluids to children, you should bolus 20ml/kg at a time to guard against fluid overload."],
                examineIfAbsent: false,
                examineIfErrors: true,
                promptsIfAbsent: "",
                promptsIfErrors: "thought that hypotension should be treated with fluid boluses regardless of etiology",
                subActionsList: false,
                subActions: []
              },
              {
                id: "intv-apply-sterile-dressings", /* not indicated intervention */
                label: "Apply sterile dressings",
                type: ACTION_TYPES.NOTIND,
                feedbackAbsent: [""],
                feedbackOutOfOrder: [""],
                feedbackErrors: ["The patient did not have any signifcant injuries to the skin."],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: []
              },
              {
                id: "intv-occlusive-dressing", /* not indicated intervention */
                label: "Occlusive dressing",
                type: ACTION_TYPES.NOTIND,
                feedbackAbsent: [""],
                feedbackOutOfOrder: [""],
                feedbackErrors: ["The patient did not have any penetrating injuries."],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: []
              },
              {
                id: "intv-bulky-dressing ", /* not indicated intervention */
                label: "Bulky dressing",
                type: ACTION_TYPES.NOTIND,
                feedbackAbsent: [""],
                feedbackOutOfOrder: [""],
                feedbackErrors: ["This patient did not have any injuries that needed to be stabilized by a bulky dressing."],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: []
              },
              {
                id: "intv-splint-fracture", /* not indicated intervention */
                label: "Splint fractures",
                type: ACTION_TYPES.NOTIND,
                feedbackAbsent: [""],
                feedbackOutOfOrder: [""],
                feedbackErrors:["The patient did not have any fractures."],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: []
              },
              {
                id: "intv-sling", /* not indicated intervention */
                label: "Apply sling",
                type: ACTION_TYPES.NOTIND,
                feedbackAbsent: [""],
                feedbackOutOfOrder: [""],
                feedbackErrors: ["The patient did not have any injuries of the upper extremities."],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: []
              },
              {
                id: "intv-prepare-amputated-part", /* not indicated intervention */
                label: "Prepare amputated part for transport",
                type: ACTION_TYPES.NOTIND,
                feedbackAbsent: [""],
                feedbackOutOfOrder: [""],
                feedbackErrors: ["The patient did not have an amputation."],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: []
              },
              {
                id: "intv-prepare-and-administer-pain-nausea-vomiting-medications", /* not indicated intervention */
                label: "Prepare and administer medications",
                type: ACTION_TYPES.NOTIND,
                feedbackAbsent: [""],
                feedbackOutOfOrder: [""],
                feedbackErrors: ["This patient is unconscious. Management of life-threatening problems takes precedence, and you should not delay addressing these to manage pain, nausea, vomiting, etc."],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: []
              },
          ],
        },     
    ],
},
}
